/**
 * Fires one `section_view` when a hub/Drop section actually reaches the viewport.
 *
 * Arrival tracking answers "did they open this page". It cannot answer "did they
 * ever get far enough down to see the leaderboard" — and on a surface that is a
 * stack of a dozen slots, that second question is the one that says where people
 * lose interest.
 *
 * Rendered ≠ seen. Every slot on the page mounts whether or not anyone scrolls to
 * it, so counting mounts would report 100% for every section and mean nothing.
 * IntersectionObserver is what makes the number honest.
 *
 * Once per mount, deliberately: a second observation when someone scrolls back up
 * would make "sections seen" depend on scroll fidgeting rather than reach.
 */

import {useEffect, useRef} from "react";
import {RouteEntryMethod} from "@devour/client";
import analytics from "@/utils/analytics";

/** Fraction of the slot that must be visible before it counts as seen. */
const VISIBLE_RATIO = 0.3;

export function useTrackSectionView(section: string | undefined, route: string): (node: HTMLElement | null) => void {
    const sent = useRef(false);
    const observer = useRef<IntersectionObserver | null>(null);

    useEffect(() => () => observer.current?.disconnect(), []);

    /*
     * Returns a ref CALLBACK rather than a ref object: slots mount and unmount as the
     * surface re-renders, and a callback observes whatever node is current instead of
     * silently holding a detached one.
     */
    return (node: HTMLElement | null): void => {
        if (!node || !section || sent.current) return;
        if (typeof IntersectionObserver === "undefined") return;

        /*
         * Step to the first element child when the node itself generates no box.
         * The caller wraps slots in `display: contents` to stay layout-neutral, and
         * such an element has no border box at all — 0x0, measured — so the observer
         * would compute a ratio of 0 forever and never fire. Guarded by a real
         * measurement rather than by testing for the property, so any other
         * zero-area wrapper is handled the same way.
         */
        let target: HTMLElement = node;
        if (target.getBoundingClientRect().width === 0 && target.getBoundingClientRect().height === 0) {
            const child = node.firstElementChild;
            if (!(child instanceof HTMLElement)) return;
            target = child;
        }

        observer.current?.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting || sent.current) continue;
                sent.current = true;
                observer.current?.disconnect();
                void analytics.trackArrival({
                    route,
                    path: window.location.pathname,
                    /*
                     * A section is never an entry point — the page arrival already
                     * recorded how they got here, and this row is about depth.
                     */
                    entryMethod: RouteEntryMethod.navigation,
                    section,
                });
            }
        }, {threshold: VISIBLE_RATIO});

        observer.current.observe(target);
    };
}
